import React from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  required,
  email,
} from 'react-admin';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import RolePermissionManager from '../../components/RolePermissionManager';
import { useRolePermissionTransform } from '../../hooks/useRolePermissionTransform';

const ClientCreateForm = () => {
  return (
    <SimpleForm
      sx={{
        width: '100%',
        '& .RaSimpleForm-form': {
          '& .MuiCard-root': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: 3,
          },
        },
      }}
    >
      {/* Información Básica del Cliente */}
      <Card sx={{ mb: 3, width: '100%' }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: '#2196F3' }}>
              <BusinessIcon />
            </Avatar>
          }
          title='Información del Cliente'
          subheader='Datos básicos de identificación del cliente'
        />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextInput
              source='clientName'
              label='Nombre del Cliente'
              validate={required()}
              fullWidth
              helperText='Nombre completo o razón social del cliente'
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Información de Contacto */}
      <Card sx={{ mb: 3, width: '100%' }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: '#4CAF50' }}>
              <PersonIcon />
            </Avatar>
          }
          title='Información de Contacto'
          subheader='Datos de contacto del cliente'
        />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextInput
              source='clientEmail'
              label='Correo Electrónico'
              type='email'
              validate={[email()]}
              fullWidth
              helperText='Dirección de correo electrónico principal'
              InputProps={{
                startAdornment: (
                  <EmailIcon sx={{ color: 'action.active', mr: 1 }} />
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: 2,
                },
              }}
            />

            <TextInput
              source='clientPhoneContact'
              label='Teléfono de Contacto'
              fullWidth
              helperText='Número de teléfono principal del cliente'
              InputProps={{
                startAdornment: (
                  <PhoneIcon sx={{ color: 'action.active', mr: 1 }} />
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Componente Universal de Roles y Permisos */}
      <RolePermissionManager
        title="Roles y Permisos del Cliente"
        subtitle="Asignación de roles y niveles de permisos para el cliente"
        helperText="Los roles determinan qué usuarios pueden acceder a este cliente y qué acciones pueden realizar."
      />

      <Divider sx={{ my: 3 }} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='body2' color='textSecondary'>
          * Campos obligatorios
        </Typography>
      </Box>
    </SimpleForm>
  );
};

const ClientsCreate = () => {
  const { transformRoles } = useRolePermissionTransform();

  const handleTransform = (data: any) => {
    return transformRoles(data);
  };

  return (
    <Create
      title='Crear Nuevo Cliente'
      redirect='show'
      transform={handleTransform}
      sx={{
        '& .RaCreate-main': {
          '& .MuiCard-root': {
            boxShadow: 'none',
            backgroundColor: 'transparent',
          },
        },
      }}
    >
      <ClientCreateForm />
    </Create>
  );
};

export default ClientsCreate;
